/**
 * statsEngine — shared stats and deviation calculation engine.
 *
 * Single source of truth for measurement statistics and deviation detection.
 *
 * USED BY
 *   Mobile BLE mode  : components/data/ProbeStatistics.tsx
 *   Mobile Hub mode  : app/(tabs)/loggers/data.tsx  (hubHistoryPatched)
 *   Mobile export    : hooks/useExport.ts
 *   Hub web UI       : hub/web/static/history.js  (mirrors this algorithm)
 *   Hub API (Python) : hub/minmax_hub/statistics.py  (must stay in sync)
 *
 * ─── CONTRACT ───────────────────────────────────────────────────────────────
 *
 * Input contract
 *   temps[] and times[] are parallel arrays, sorted ascending by time.
 *   times are Unix seconds (float). temps are °C (float).
 *
 * Measurement stats
 *   count            : total number of samples
 *   min / max / avg  : simple min, max, arithmetic mean
 *   std_dev          : POPULATION std dev  ÷ n  (not sample ÷ n-1)
 *   avg_interval     : (last_time − first_time) / (n − 1)  in minutes
 *                      null when n ≤ 1
 *
 * Deviation counting
 *   A deviation starts when a sample crosses a threshold (temp > alarmHigh
 *   OR temp < alarmLow) coming from an in-range state.
 *   Consecutive out-of-range samples belong to the SAME deviation.
 *   A new deviation is counted only on the next in→out transition.
 *
 * Deviation duration
 *   duration = time_of_first_in_range_sample − time_of_first_out_of_range_sample
 *   (real timestamps, NOT n_samples × nominal_interval)
 *   If still out-of-range at end of array, last_time is used as closing time.
 *
 * Amplitude
 *   Each out-of-range sample contributes its individual excess:
 *     high excess = temp − alarmHigh  (per sample, not just peak)
 *     low  excess = alarmLow − temp   (per sample, not just trough)
 *   avgDeviationHigh / avgDeviationLow = mean of ALL individual sample excesses
 *   maxDeviationHigh / maxDeviationLow = max individual sample excess
 *
 * Any implementation in another language MUST produce identical results.
 * ────────────────────────────────────────────────────────────────────────────
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MeasurementStats {
  count: number;
  min_temp: number;
  max_temp: number;
  avg_temp: number;
  std_dev: number;
  avg_interval_minutes: number | null;
}

export interface DeviationStats {
  count: number;
  totalTimeSeconds: number;
  avgTimeSeconds: number;
  maxTimeSeconds: number;
  avgDeviationHigh: number;
  avgDeviationLow: number;
  maxDeviationHigh: number;
  maxDeviationLow: number;
}

export interface Thresholds {
  alarmHigh: number | null;
  alarmLow: number | null;
}

// ---------------------------------------------------------------------------
// Measurement statistics
// ---------------------------------------------------------------------------

export function computeMeasurementStats(
  temps: number[],
  times: number[], // unix seconds, sorted ascending
): MeasurementStats {
  const count = temps.length;
  const min_temp = Math.min(...temps);
  const max_temp = Math.max(...temps);
  const avg_temp = temps.reduce((s, v) => s + v, 0) / count;
  const variance = temps.reduce((s, v) => s + Math.pow(v - avg_temp, 2), 0) / count;
  const std_dev = Math.sqrt(variance);
  const avg_interval_minutes = count > 1
    ? (times[count - 1] - times[0]) / (count - 1) / 60
    : null;
  return { count, min_temp, max_temp, avg_temp, std_dev, avg_interval_minutes };
}

// ---------------------------------------------------------------------------
// Deviation detection
// ---------------------------------------------------------------------------

export function computeDeviations(
  temps: number[],
  times: number[], // unix seconds, sorted ascending
  thresholds: Thresholds,
): DeviationStats {
  const { alarmHigh, alarmLow } = thresholds;

  let devCount = 0;
  let totalTimeSec = 0;
  let maxTimeSec = 0;
  let currentStart: number | null = null;
  const highAmps: number[] = [];
  const lowAmps: number[] = [];

  for (let i = 0; i < temps.length; i++) {
    const temp = temps[i];
    const time = times[i];
    const isAboveHigh = alarmHigh != null && temp > alarmHigh;
    const isBelowLow  = alarmLow  != null && temp < alarmLow;
    const isDev = isAboveHigh || isBelowLow;

    if (isDev) {
      if (currentStart === null) { currentStart = time; devCount++; }
      if (isAboveHigh && alarmHigh != null) highAmps.push(temp - alarmHigh);
      if (isBelowLow  && alarmLow  != null) lowAmps.push(alarmLow - temp);
    } else {
      if (currentStart !== null) {
        const dur = time - currentStart;
        totalTimeSec += dur;
        maxTimeSec = Math.max(maxTimeSec, dur);
        currentStart = null;
      }
    }
  }

  // Close last deviation if still open at end of period
  if (currentStart !== null && times.length > 0) {
    const dur = times[times.length - 1] - currentStart;
    totalTimeSec += dur;
    maxTimeSec = Math.max(maxTimeSec, dur);
  }

  return {
    count: devCount,
    totalTimeSeconds: totalTimeSec,
    avgTimeSeconds: devCount > 0 ? Math.round(totalTimeSec / devCount) : 0,
    maxTimeSeconds: maxTimeSec,
    avgDeviationHigh: highAmps.length > 0 ? highAmps.reduce((a, b) => a + b, 0) / highAmps.length : 0,
    avgDeviationLow:  lowAmps.length  > 0 ? lowAmps.reduce((a, b)  => a + b, 0) / lowAmps.length  : 0,
    maxDeviationHigh: highAmps.length > 0 ? Math.max(...highAmps) : 0,
    maxDeviationLow:  lowAmps.length  > 0 ? Math.max(...lowAmps)  : 0,
  };
}
